import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowRightOutlined, EditOutlined, EyeOutlined, FormOutlined, MessageOutlined } from "@ant-design/icons";
import { Button, Card, Col, Divider, Layout, Row, Space, Typography } from "antd";
import AppLayout from "@/Components/Common/AppLayout";

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;

const ResumeEditingChoice = () => {
  const { resumeId } = useParams();
  const navigate = useNavigate();

  const editingOptions = [
    {
      id: "chat",
      title: "AI Chat Interface",
      description: "Use natural language to enhance your resume with AI assistance - our recommended approach",
      icon: <MessageOutlined style={{ fontSize: "48px", color: "#1890ff" }} />,
      features: [
        "Natural language commands",
        "AI-powered suggestions and analysis",
        "Preview changes before applying",
        "Conversational editing experience",
        "Smart content enhancement",
        "Professional writing assistance",
      ],
      path: `/app/resume/${resumeId}/chat`,
      buttonText: "Start AI Chat Editing",
      recommended: true,
    },
    {
      id: "modal",
      title: "Form-Based Modal Editing",
      description: "Edit resume sections through structured forms and modals",
      icon: <FormOutlined style={{ fontSize: "48px", color: "#722ed1" }} />,
      features: [
        "Structured form editing",
        "Section-by-section modals",
        "Organized data entry",
        "Professional form experience",
        "Detailed field validation",
        "Traditional editing approach",
      ],
      path: `/app/resume/${resumeId}/modal-based`,
      buttonText: "Use Modal Editor",
    },
  ];

  const handlePreviewResume = () => {
    navigate(`/app/resume/${resumeId}/preview`);
  };

  const handleMethodSelect = (path) => {
    navigate(path);
  };

  return (
    <AppLayout>
      <Layout style={{ minHeight: "100vh", background: "#f5f5f5" }}>
        <Content style={{ padding: "40px 24px" }}>
          <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
            {/* Header */}
            <div style={{ textAlign: "center", marginBottom: "40px" }}>
              <Title level={2} style={{ color: "#262626", marginBottom: "16px" }}>
                Choose Your Editing Method
              </Title>
              <Paragraph style={{ fontSize: "16px", color: "#666", maxWidth: "600px", margin: "0 auto" }}>
                Select the editing method that best suits your workflow. You can always switch between methods
                later.
              </Paragraph>
            </div>

            {/* Editing Options */}
            <Row gutter={[24, 24]} justify="center">
              {editingOptions.map((option) => (
                <Col key={option.id} xs={24} md={8}>
                  <Card
                    hoverable
                    style={{
                      height: "100%",
                      border: option.recommended ? "2px solid #1890ff" : "1px solid #d9d9d9",
                      position: "relative",
                      transition: "all 0.3s ease",
                    }}
                    bodyStyle={{
                      padding: "32px 24px",
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                    }}
                  >
                    {option.recommended && (
                      <div
                        style={{
                          position: "absolute",
                          top: "-1px",
                          right: "16px",
                          background: "#1890ff",
                          color: "white",
                          padding: "4px 12px",
                          fontSize: "12px",
                          fontWeight: "600",
                          borderRadius: "0 0 4px 4px",
                        }}
                      >
                        RECOMMENDED
                      </div>
                    )}

                    <div style={{ textAlign: "center", marginBottom: "24px" }}>
                      {option.icon}
                      <Title level={3} style={{ marginTop: "16px", marginBottom: "8px" }}>
                        {option.title}
                      </Title>
                      <Paragraph style={{ color: "#666", fontSize: "14px" }}>{option.description}</Paragraph>
                    </div>

                    <div style={{ flex: 1, marginBottom: "24px" }}>
                      <Text strong style={{ color: "#262626", marginBottom: "12px", display: "block" }}>
                        Key Features:
                      </Text>
                      <ul style={{ paddingLeft: "20px", margin: 0 }}>
                        {option.features.map((feature, index) => (
                          <li key={index} style={{ marginBottom: "8px", color: "#666" }}>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <Button
                      type={option.recommended ? "primary" : "default"}
                      size="large"
                      icon={<ArrowRightOutlined />}
                      onClick={() => handleMethodSelect(option.path)}
                      style={{
                        width: "100%",
                        height: "48px",
                        fontSize: "16px",
                        fontWeight: "500",
                      }}
                    >
                      {option.buttonText}
                    </Button>
                  </Card>
                </Col>
              ))}
            </Row>

            <Divider style={{ margin: "48px 0" }} />

            {/* Preview Option */}
            <div style={{ textAlign: "center" }}>
              <Card style={{ maxWidth: "500px", margin: "0 auto" }}>
                <Space direction="vertical" size="large" style={{ width: "100%" }}>
                  <div style={{ textAlign: "center" }}>
                    <EyeOutlined style={{ fontSize: "36px", color: "#1890ff", marginBottom: "16px" }} />
                    <Title level={4} style={{ marginBottom: "8px" }}>
                      Just Want to Preview?
                    </Title>
                    <Text type="secondary">View your resume in different templates without making changes</Text>
                  </div>

                  <Button
                    type="primary"
                    size="large"
                    icon={<EyeOutlined />}
                    onClick={handlePreviewResume}
                    style={{ width: "100%" }}
                  >
                    Preview Resume Templates
                  </Button>
                </Space>
              </Card>
            </div>

            {/* Help Text */}
            <div style={{ textAlign: "center", marginTop: "32px" }}>
              <Text type="secondary" style={{ fontSize: "14px" }}>
                💡 Tip: You can switch between editing methods anytime from the navigation menu
              </Text>
            </div>
          </div>
        </Content>
      </Layout>
    </AppLayout>
  );
};

export default ResumeEditingChoice;
